import React from "react";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = Date.now().toString();
    
    this.setState({
      error: error,
      errorInfo: errorInfo,
      errorId: errorId
    });

    // Log error details
    console.error("React Error Boundary caught an error:", error, errorInfo);
    
    // Call parent error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // In production, you might want to send this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      // Example: sendErrorReport(error, errorInfo, errorId);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center text-white p-8">
          <div className="text-center max-w-lg">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>

            <h1 className="text-3xl font-bold mb-4">Something went wrong</h1>

            <p className="text-white/70 mb-6 leading-relaxed">
              The application encountered an unexpected error. This might be due to a temporary issue or a bug in the code.
            </p>

            <div className="space-y-3 mb-6">
              <button
                onClick={this.handleReload}
                className="bg-purple-600 hover:bg-purple-700 px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
              >
                <RotateCcw className="w-5 h-5" />
                Reload Application
              </button>

              <button
                onClick={this.handleReset}
                className="bg-gray-600 hover:bg-gray-700 px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
              >
                <Home className="w-5 h-5" />
                Try to Continue
              </button>
            </div>

            <details className="text-left bg-black/20 rounded-lg p-4 mt-6">
              <summary className="cursor-pointer text-sm text-white/60 mb-3 hover:text-white/80">
                Error Details (for developers)
              </summary>
              
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-semibold text-red-300 mb-1">Error ID:</div>
                  <div className="text-xs text-white/60 font-mono">{this.state.errorId}</div>
                </div>
                
                <div>
                  <div className="text-xs font-semibold text-red-300 mb-1">Error Message:</div>
                  <pre className="text-xs text-red-300 whitespace-pre-wrap font-mono bg-red-500/10 p-2 rounded">
                    {this.state.error && this.state.error.toString()}
                  </pre>
                </div>
                
                <div>
                  <div className="text-xs font-semibold text-red-300 mb-1">Component Stack:</div>
                  <pre className="text-xs text-white/60 whitespace-pre-wrap font-mono bg-black/20 p-2 rounded max-h-40 overflow-y-auto">
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </div>
              </div>
            </details>

            <div className="mt-6 text-xs text-white/40">
              If this error persists, please report it with the error ID above.
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;